import React from 'react'
import PropTypes from 'prop-types'
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { useTranslate } from 'react-admin'
import playerStatsTableStyles from './playerStatsTable.styles'
import { getVisibleStatKeys } from '../../utils/helpers/displayStatsMapping'

const fmtRatio = (val, total) => (val == 0 ? '-' : `${val}/${total}`)
const fmtMinutes = (val) => {
  if (!val) return '00:00'
  const [hh, mm, ss] = val.split(':')
  const totalMinutes = parseInt(hh, 10) * 60 + parseInt(mm, 10)
  return `${totalMinutes}:${ss.split('.')[0]}`
}

const seasonColumnsConfig = [
  { key: 'gamesPlayed', label: 'GP', getTotal: ps => ps.gamesPlayed, getAvg: ps => ps.gamesPlayed, alwaysVisible: true },
  { key: 'gamesStarted', label: 'GS', getTotal: ps => ps.gamesStarted, getAvg: ps => ps.gamesStarted, alwaysVisible: true },
  { key: 'minutes', label: 'MIN', getTotal: ps => fmtMinutes(ps.minutes), getAvg: ps => fmtMinutes(ps.avgMinutes) },
  { key: 'points', label: 'PTS', getTotal: ps => ps.points, getAvg: ps => ps.avgPoints },
  { key: 'twoPointer', label: '2P', getTotal: ps => ps.made2Pointers, getAvg: ps => ps.avgMade2Pointers },
  { key: 'threePointer', label: '3P', getTotal: ps => ps.made3Pointers, getAvg: ps => ps.avgMade3Pointers },
  { key: 'freeThrows', label: 'FT', getTotal: ps => ps.made1Pointers, getAvg: ps => ps.avgMade1Pointers },
  { key: 'offensiveRebounds', label: 'OR', getTotal: ps => ps.offensiveRebounds, getAvg: ps => ps.avgOffensiveRebounds },
  { key: 'defensiveRebounds', label: 'DR', getTotal: ps => ps.defensiveRebounds, getAvg: ps => ps.avgDefensiveRebounds },
  { key: 'totalRebounds', label: 'TR', getTotal: ps => ps.rebounds, getAvg: ps => ps.avgRebounds },
  { key: 'assists', label: 'AST', getTotal: ps => ps.assists, getAvg: ps => ps.avgAssists },
  { key: 'steals', label: 'STL', getTotal: ps => ps.steals, getAvg: ps => ps.avgSteals },
  { key: 'turnOvers', label: 'TO', getTotal: ps => ps.turnovers, getAvg: ps => ps.avgTurnovers },
  { key: 'blocksAgainst', label: 'BLKA', getTotal: ps => ps.blocksAgainst, getAvg: ps => ps.avgBlocksAgainst },
  { key: 'foulsCommited', label: 'FC', getTotal: ps => ps.fouls, getAvg: ps => ps.avgFouls },
  { key: 'foulsDrawn', label: 'FD', getTotal: ps => ps.foulsDrawn, getAvg: ps => ps.avgFoulsDrawn },
  { key: 'pir', label: 'PIR', getTotal: ps => ps.pir, getAvg: ps => ps.avgPir },
]

const gameColumnsConfig = [
  { key: 'minutes', label: 'MIN', getValue: ps => fmtMinutes(ps.minutes) },
  { key: 'points', label: 'Pts', getValue: ps => ps.points },
  { key: 'twoPointer', label: 'FG2', getValue: ps => fmtRatio(ps.made2Pointers, ps.attempted2Pointers) },
  { key: 'threePointer', label: 'FG3', getValue: ps => fmtRatio(ps.made3Pointers, ps.attempted3Pointers) },
  { key: 'freeThrows', label: 'FT', getValue: ps => fmtRatio(ps.made1Pointers, ps.attempted1Pointers) },
  { key: 'offensiveRebounds', label: 'OR', getValue: ps => ps.offensiveRebounds },
  { key: 'defensiveRebounds', label: 'DR', getValue: ps => ps.defensiveRebounds },
  { key: 'totalRebounds', label: 'TR', getValue: ps => ps.rebounds },
  { key: 'assists', label: 'As', getValue: ps => ps.assists },
  { key: 'steals', label: 'St', getValue: ps => ps.steals },
  { key: 'turnOvers', label: 'To', getValue: ps => ps.turnovers },
  { key: 'blocksAgainst', label: 'BLKA', getValue: ps => ps.blocksAgainst },
  { key: 'foulsCommited', label: 'FC', getValue: ps => ps.fouls },
  { key: 'foulsDrawn', label: 'FD', getValue: ps => ps.foulsDrawn },
  { key: 'pir', label: 'PIR', getValue: ps => ps.pir },
]

const PlayerStatsTable = ({ playerData, displayStats }) => {
  const useStyles = makeStyles(playerStatsTableStyles)
  const classes = useStyles()
  const translate = useTranslate()

  const player = playerData.playerStatsDto
  const playerStats = player.stats

  // displayStats filtering — null means show all (same as prod)
  const filterableKeys = seasonColumnsConfig.filter(c => !c.alwaysVisible).map(c => c.key)
  const visibleKeys = getVisibleStatKeys(displayStats, filterableKeys)

  const filteredSeasonColumns = visibleKeys
    ? seasonColumnsConfig.filter(col => col.alwaysVisible || visibleKeys.includes(col.key))
    : seasonColumnsConfig

  const filteredGameColumns = visibleKeys
    ? gameColumnsConfig.filter(col => visibleKeys.includes(col.key))
    : gameColumnsConfig

  const games = (playerData.teamPlayerStatsPerGame || [])
    .filter((game) => game.opponentName !== null)
    .map((game) => {
      const gameVisibleKeys = getVisibleStatKeys(game.displayStats, filterableKeys)
      return {
        opponentName: game.opponentName,
        didPlay: game.played,
        stats: game.stats,
        visibleKeys: gameVisibleKeys,
      }
    })

  const playedGames = games.filter(g => g.didPlay)
  const didNotPlayGames = games.filter(g => !g.didPlay)
  const shouldShowGameStatsTable = games.length > 0

  return (
    <div>
      <Typography variant='h6' gutterBottom className={classes.marginTypo}>
        {translate('ra.text.seasonStats')}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableCell} />
              {filteredSeasonColumns.map(col => (
                <TableCell key={col.key} className={classes.tableCell}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.typo}>Total</Typography>
              </TableCell>
              {filteredSeasonColumns.map(col => (
                <TableCell key={col.key} className={classes.tableCell}>
                  {col.getTotal(playerStats)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <Typography className={classes.typo}>Average</Typography>
              </TableCell>
              {filteredSeasonColumns.map(col => (
                <TableCell key={col.key} className={classes.tableCell}>
                  {col.getAvg(playerStats)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {shouldShowGameStatsTable && (
        <>
          <Typography variant='h6' gutterBottom className={classes.marginTypo}>
            {translate('ra.text.gameStats')}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  <TableCell className={classes.tableCell}>
                  </TableCell>
                  {filteredGameColumns.map(col => (
                    <TableCell key={col.key} className={classes.tableCell}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {playedGames.map((game, index) => (
                  <TableRow key={index}>
                    <TableCell className={classes.tableCell}>
                      vs {game.opponentName}
                    </TableCell>
                    {filteredGameColumns.map(col => (
                      <TableCell key={col.key} className={classes.tableCell}>
                        {game.visibleKeys && !game.visibleKeys.includes(col.key)
                          ? '-'
                          : col.getValue(game.stats)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {didNotPlayGames.map((game) => (
                  <TableRow key={game.opponentName}>
                    <TableCell className={classes.tableCell}>
                      vs {game.opponentName}
                    </TableCell>
                    <TableCell colSpan={filteredGameColumns.length} className={classes.tableCell}>
                      {translate('ra.text.didNotPlay')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  )
}

PlayerStatsTable.propTypes = {
  playerData: PropTypes.shape({
    playerStatsDto: PropTypes.shape({
      playerId: PropTypes.string,
      fullName: PropTypes.string,
      stats: PropTypes.object,
    }),
    teamPlayerStatsPerGame: PropTypes.arrayOf(
      PropTypes.shape({
        playerId: PropTypes.string,
        fullName: PropTypes.string,
        opponentName: PropTypes.string,
        stats: PropTypes.object,
      }),
    ),
  }),
  displayStats: PropTypes.array,
}

export default PlayerStatsTable
